import {
	parseDynamicDocumentDefinitions,
	processDates,
} from "./Utils/DateUtils.js";
import ClickhouseDateTimeGranularity = App.Enums.ClickhouseDateTimeGranularity;
import DocumentDO = App.Data.Explorer.DocumentDO;
import dayjs from "dayjs";
import { HorizontalScaleRenderMode } from "./Scales/xScale.js";
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;
import isSameOrBefore from "dayjs/plugin/isSameOrBefore.js";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter.js";
import duration from "dayjs/plugin/duration.js";
import relativeTime from "dayjs/plugin/relativeTime.js";
import en from "dayjs/locale/en.js";
import weekOfYear from "dayjs/plugin/weekOfYear.js";
import weekday from "dayjs/plugin/weekday.js";
import timezone from "dayjs/plugin/timezone.js";
import utc from "dayjs/plugin/utc.js";
import isoWeek from "dayjs/plugin/isoWeek.js";
import quarter from "dayjs/plugin/quarterOfYear.js";
import advancedFormat from "dayjs/plugin/advancedFormat.js";
import customParseFormat from "dayjs/plugin/customParseFormat.js";

dayjs.extend(isoWeek);
dayjs.extend(quarter);
dayjs.extend(advancedFormat);

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

dayjs.extend(weekOfYear);
dayjs.extend(weekday);

dayjs.extend(duration);
dayjs.extend(relativeTime);

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Amsterdam");

dayjs.extend(customParseFormat);

dayjs.locale(en);

export const processedDocument = (document: DocumentDO) => {
	return processDates(
		parseDynamicDocumentDefinitions(document),
		{} as App.Data.Explorer.DocumentDO
	);
};

export const dimensionRenderMode = (
	dimension: DocumentDimensionDTO
): HorizontalScaleRenderMode => {
	switch (dimension.granularity) {
		case "day":
			return HorizontalScaleRenderMode.DATE_WITHOUT_YEAR;

		case "week":
			return HorizontalScaleRenderMode.WEEK_WITHOUT_YEAR;

		case "month":
			return HorizontalScaleRenderMode.MONTH_WITHOUT_YEAR;

		case "quarter":
			return HorizontalScaleRenderMode.QUARTER_WITHOUT_YEAR;

		default:
			return HorizontalScaleRenderMode.DEFAULT;
	}
};

export const hasAdditionalYearAxis = (derivedDocument: DocumentDO) => {
	return (
		dimensionRenderMode(derivedDocument.horizontal_dimension) !==
			HorizontalScaleRenderMode.DEFAULT &&
		!derivedDocument.is_horizontal_sorting_enabled
	);
};

export const hasOffsetDimension = (derivedDocument: DocumentDO) => {
	return derivedDocument.offset_dimension !== null;
};

export const horizontalDateGranularity = (derivedDocument: DocumentDO) => {
	return derivedDocument.horizontal_dimension
		.granularity as ClickhouseDateTimeGranularity;
};

export const offsetDateGranularity = (derivedDocument: DocumentDO) => {
	return derivedDocument.offset_dimension
		.granularity as ClickhouseDateTimeGranularity;
};

export const horizontalSupportsPeriodTicks = (derivedDocument: DocumentDO) => {
	return (
		dimensionRenderMode(derivedDocument.horizontal_dimension) !==
			HorizontalScaleRenderMode.DEFAULT &&
		["day", "month"].includes(horizontalDateGranularity(derivedDocument))
	);
};

export const offsetSupportsPeriodTicks = (derivedDocument: DocumentDO) => {
	return (
		dimensionRenderMode(derivedDocument.offset_dimension) !==
			HorizontalScaleRenderMode.DEFAULT &&
		["day", "month"].includes(offsetDateGranularity(derivedDocument))
	);
};

export const horizontalSupportsAnnotationBubbles = (
	derivedDocument: DocumentDO
) => {
	return (
		dimensionRenderMode(derivedDocument.horizontal_dimension) !==
			HorizontalScaleRenderMode.DEFAULT &&
		["day", "week", "month", "quarter"].includes(
			horizontalDateGranularity(derivedDocument)
		)
	);
};

export const supportsGranularityMarking = (derivedDocument: DocumentDO) => {
	return (
		derivedDocument?.horizontal_dimension.type === "datetime" &&
		derivedDocument.horizontal_dimension.family_name_clean ===
			"periodsubjectdate"
	);
};

function getWeekBounds(weekStr: string): [Date, Date] {
	const [yearStr, weekNumberStr] = weekStr.split("-");
	const year = Number(yearStr);
	const week = Number(weekNumberStr);

	if (isNaN(year) || isNaN(week)) return null;

	const start = dayjs()
		.year(year)
		.isoWeek(week)
		.startOf("isoWeek")
		.startOf("day");
	const end = dayjs().year(year).isoWeek(week).endOf("isoWeek").endOf("day");
	return [start.toDate(), end.toDate()];
}

function getQuarterBounds(quarterStr: string): [Date, Date] {
	const [yearStr, quarterNumberStr] = quarterStr.split("-Q");
	const year = Number(yearStr);
	const quarter = Number(quarterNumberStr);

	if (isNaN(year) || isNaN(quarter)) return null;

	const start = dayjs().year(year).quarter(quarter).startOf("quarter");
	const end = dayjs().year(year).quarter(quarter).endOf("quarter");
	return [start.toDate(), end.toDate()];
}

function granuleBounds(
	granularity: string,
	dateDimension: string | Date
): [Date, Date] | null {
	if (granularity === "week") {
		return getWeekBounds(dateDimension);
	} else if (granularity === "quarter") {
		return getQuarterBounds(dateDimension);
	}
	const d = dayjs(dateDimension);

	// Does not support any aggregate granularities like Day of week, 60min, 30min etc.
	// checking for aggregate granularities is not relevant.
	const start = {
		day: d.startOf("day"),
		month: d.startOf("month").startOf("day"),
		year: d.startOf("year").startOf("day"),
	}[granularity];

	const end = {
		day: d.endOf("day"),
		month: d.endOf("month").endOf("day"),
		year: d.endOf("year").endOf("day"),
	}[granularity];

	if (!start || !end) {
		return null;
	}
	return [start.toDate(), end.toDate()];
}

export function markIncompleteGranule(
	value: string,
	granularity: string,
	derivedDocument: DocumentDO
): boolean {
	const fromParsed = derivedDocument.period_selection.from_parsed;
	const toParsed = derivedDocument.period_selection.to_parsed;
	const bounds = granuleBounds(granularity, value);

	if (!bounds) {
		return false;
	}

	const [start, end] = bounds;
	const isComplete =
		dayjs(fromParsed).tz("Europe/Amsterdam").isSameOrBefore(start) &&
		dayjs(toParsed).tz("Europe/Amsterdam").add(1, "second").isSameOrAfter(end);

	return !isComplete;
}
